export enum UserRole {
  ADMIN = 'ADMIN',
  SUPERVISOR = 'SUPERVISOR',
  AGENT = 'AGENT',
  VIEWER = 'VIEWER',
}

export interface User {
  id: string;
  username: string;
  password?: string;
  email?: string;
  role: UserRole;
  authCode: string;
  pushSubscriptions?: { [key: string]: any };
  twoFactorEnabled?: boolean;
  twoFactorSecret?: string;
  twoFactorRecoveryCodes?: string[];
}

export enum Tab {
  Overview = 'Overview',
  CrewMovement = 'Crew Movement',
  CounterDistribution = 'Counter Distribution',
  Security = 'Security',
  MyAccount = 'My Account',
  Reports = 'Reports',
  Admin = 'Admin Panel',
}

export const STATIONS = [
  'Crew Arrived',
  '1st X-ray',
  'Immigration In',
  'Immigration Out',
  'Duty Free',
  'At Gate',
  'At Aircraft',
  'Ready For Boarding',
  '1st Pax IN',
  'Last Pax',
  'Doors Closed',
  'Push Back',
] as const;

export type Station = typeof STATIONS[number];
export type NotificationStation = Station | 'Acceptance Finalized' | 'System Clear' | 'Gate Clear';

export interface TimestampEntry {
  time: string; // ISO string
  modified: boolean;
  by: string; // username
}

export type CrewMovementData = {
  [key in Station]?: {
    crew: TimestampEntry[];
    pilots: TimestampEntry[];
  };
};

export interface CounterDistributionData {
  etd: string;
  flightNo: string;
  booking: {
    c: string;
    m: string;
    inf: string;
    totalPassengers: string;
  };
  counters: { [key: string]: string };
  gates: { [key: string]: string };
  finalFigures: {
    c: string;
    m: string;
    inf: string;
    totalPassengers: string;
    'Total Bags': string;
    'Crew Bags': string;
    'Waved Bags': string;
    'Wheel Chairs': string;
    'Buggy': string;
    'Final Fuel': string;
  };
}

export interface ScannedPassenger {
  sequence: number;
  name: string;
  seatNumber: string;
  note: string;
  scannedAt: string; // ISO string
  scannedBy: string; // username
}

export interface NotificationData {
  station: NotificationStation;
  time: string; // The time string to display
  timestamp: number; // A unique timestamp (e.g., Date.now()) to identify the notification
  triggeredBy: string;
}

export interface ATSRData {
  atsrText: string;
  excessBaggage: string;
  heavyBags: string;
  notes: string;
}

export interface PwaIcon {
  src: string;
  type: string;
  sizes: string;
  purpose?: 'any' | 'maskable';
}

export interface FlightData {
  crewMovement: CrewMovementData;
  counterDistribution: CounterDistributionData;
  immigrationApproved: boolean;
  immigrationComment: string;
  crewAndPilotsArrivedTogether: boolean;
  capturedDocs: { url: string; capturedAt: string; by: string }[];
  newsFeed?: { [id: string]: Omit<NewsMessage, 'id'> };
  scannedPassengers?: ScannedPassenger[];
  lastNotification?: NotificationData | null;
  atsrData?: ATSRData;
  acceptanceFinalized?: {
    time: string; // ISO string
    by: string; // username
  };
  systemClear?: {
    time: string; // ISO string
    by: string; // username
  };
  gateClear?: {
    time: string; // ISO string
    by: string; // username
  };
}

export const initialFlightData: FlightData = {
  crewMovement: {},
  counterDistribution: {
    etd: '',
    flightNo: '',
    booking: {
      c: '',
      m: '',
      inf: '',
      totalPassengers: '0',
    },
    counters: {
      'Counter 1': '', 'Counter 2': '', 'Counter 3': '', 'Counter 4': '', 
      'Counter 5': '', 'Counter 6': '', 'Counter 7': '', 'Counter 8 (Excess)': ''
    },
    gates: {
      'Immigration': '', 'Gate Control': '', 'System-Visa Check': '', 
      'Crew 1 (Crew)': '', 'Crew 2 (Pilots)': '', 'Que Control': '', 'Jetty': '', 'Custom': ''
    },
    finalFigures: {
      c: '',
      m: '',
      inf: '',
      totalPassengers: '0',
      'Total Bags': '', 
      'Crew Bags': '', 
      'Waved Bags': '', 
      'Wheel Chairs': '', 
      'Buggy': '',
      'Final Fuel': ''
    }
  },
  immigrationApproved: false,
  immigrationComment: '',
  crewAndPilotsArrivedTogether: false,
  capturedDocs: [],
  newsFeed: {},
  scannedPassengers: [],
  lastNotification: null,
  atsrData: {
    atsrText: '',
    excessBaggage: '',
    heavyBags: '',
    notes: '',
  },
};

export interface FlightRecord extends FlightData {
  id: string; // flightNo-date
  flightDate: string;
}

export interface NewsMessage {
  id: string;
  text: string;
  sender: string;
  timestamp: number;
}

export interface LoginHistoryLog {
  userId: string;
  username: string;
  action: 'login' | 'logout';
  timestamp: number;
  ip?: string;
  city?: string;
  region?: string;
  countryName?: string;
}

export interface SessionHistoryLog {
  key: string;
  userId: string;
  username: string;
  timestamp: number;
}